// 🔧 agent_settings.js
// ✅ Purpose: Admin-facing AI Agent Settings page for Let's Go Vote Sales Automation
// ✅ Features:
// - Manages Collection Manager daily limits
// - Manages minimum preferred and fallback fit scores
// - Manages follow-up timing defaults
// - Stores settings in Firestore `sales_agent_settings/main`
// - Keeps automation configurable without code changes
// - Does NOT run agents or send messages
// - Uses shared SalesPageLayout
// - Fully block-commented for clarity and future devs

import React, { useEffect, useState } from "react";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import SalesPageLayout from "../../components/sales/SalesPageLayout";

const defaultSettings = {
  automationEnabled: false,

  dailyOutreachGoal: 10,
  researchLimitPerRun: 3,
  draftLimitPerRun: 5,

  preferredMinimumFitScore: 8,
  fallbackMinimumFitScore: 5,

  followUpDaysAfterFirstContact: 7,
  followUpDaysAfterFollowUp: 14,

  allowEmailChannel: true,
  allowFacebookChannel: true,
  allowInstagramChannel: true,
  allowContactFormChannel: true,

  humanApprovalRequired: true,
};

function AgentSettings() {
  const [settings, setSettings] = useState(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [status, setStatus] = useState(null);

  const showStatus = (type, message) => {
    setStatus({ type, message });
    setTimeout(() => setStatus(null), 20000);
  };

  const loadSettings = async () => {
    try {
      setLoading(true);

      const settingsRef = doc(db, "sales_agent_settings", "main");
      const settingsSnap = await getDoc(settingsRef);

      if (settingsSnap.exists()) {
        setSettings({
          ...defaultSettings,
          ...settingsSnap.data(),
        });
      } else {
        setSettings(defaultSettings);
      }
    } catch (error) {
      console.error("❌ Failed to load Agent Settings:", error);
      showStatus("error", "Failed to load Agent Settings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveSettings = async (event) => {
    event.preventDefault();

    try {
      setSaving(true);

      await setDoc(
        doc(db, "sales_agent_settings", "main"),
        {
          ...settings,

          dailyOutreachGoal: Number(settings.dailyOutreachGoal) || 10,
          researchLimitPerRun: Math.min(
            15,
            Math.max(1, Number(settings.researchLimitPerRun) || 3)
          ),
          draftLimitPerRun: Number(settings.draftLimitPerRun) || 5,
          preferredMinimumFitScore:
            Number(settings.preferredMinimumFitScore) || 8,
          fallbackMinimumFitScore:
            Number(settings.fallbackMinimumFitScore) || 5,
          followUpDaysAfterFirstContact:
            Number(settings.followUpDaysAfterFirstContact) || 7,
          followUpDaysAfterFollowUp:
            Number(settings.followUpDaysAfterFollowUp) || 14,

          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showStatus("success", "Agent Settings saved successfully.");
    } catch (error) {
      console.error("❌ Failed to save Agent Settings:", error);
      showStatus("error", "Failed to save Agent Settings.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <SalesPageLayout
      title="Agent Settings"
      subtitle="Configure LGV Sales Automation limits, scoring, channels, and approval rules."
      status={status}
      actions={
        <>
          <Link className="btn btn-outline-primary" to="/admin/ai-mission-control">
            AI Mission Control
          </Link>

          <Link className="btn btn-outline-secondary" to="/admin/sales-dashboard">
            Sales Dashboard
          </Link>
        </>
      }
    >
      {loading ? (
        <div className="alert alert-info">Loading Agent Settings...</div>
      ) : (
        <form onSubmit={handleSaveSettings}>
          <div className="card mb-4">
            <div className="card-header">
              <strong>Collection Manager</strong>
            </div>

            <div className="card-body">
              <div className="form-check form-switch mb-3">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="automationEnabled"
                  checked={settings.automationEnabled}
                  onChange={(e) =>
                    handleChange("automationEnabled", e.target.checked)
                  }
                />

                <label className="form-check-label" htmlFor="automationEnabled">
                  Enable scheduled automation later
                </label>

                <div className="small text-muted">
                  This setting is saved now. The scheduled timer will respect it
                  when added.
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label">Daily Outreach Goal</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="100"
                    value={settings.dailyOutreachGoal}
                    onChange={(e) =>
                      handleChange("dailyOutreachGoal", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Research Limit Per Run</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="15"
                    value={settings.researchLimitPerRun}
                    onChange={(e) =>
                      handleChange("researchLimitPerRun", e.target.value)
                    }
                  />
                  <div className="form-text">Maximum 15 to stay within the workflow timeout.</div>
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label">Draft Limit Per Run</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="25"
                    value={settings.draftLimitPerRun}
                    onChange={(e) =>
                      handleChange("draftLimitPerRun", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Priority Rules</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Preferred Minimum Fit Score</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="10"
                    value={settings.preferredMinimumFitScore}
                    onChange={(e) =>
                      handleChange("preferredMinimumFitScore", e.target.value)
                    }
                  />

                  <div className="small text-muted">
                    The engine will try to fill the queue from this score and above first.
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">Fallback Minimum Fit Score</label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="10"
                    value={settings.fallbackMinimumFitScore}
                    onChange={(e) =>
                      handleChange("fallbackMinimumFitScore", e.target.value)
                    }
                  />

                  <div className="small text-muted">
                    If high-score prospects run low, the engine can use this floor
                    to keep the queue moving.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Channel Rules</strong>
            </div>

            <div className="card-body">
              {[
                ["allowEmailChannel", "Email"],
                ["allowFacebookChannel", "Facebook"],
                ["allowInstagramChannel", "Instagram"],
                ["allowContactFormChannel", "Website Contact Form"],
              ].map(([field, label]) => (
                <div className="form-check mb-2" key={field}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id={field}
                    checked={settings[field]}
                    onChange={(e) => handleChange(field, e.target.checked)}
                  />

                  <label className="form-check-label" htmlFor={field}>
                    Allow {label}
                  </label>
                </div>
              ))}

              <hr />

              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="humanApprovalRequired"
                  checked={settings.humanApprovalRequired}
                  onChange={(e) =>
                    handleChange("humanApprovalRequired", e.target.checked)
                  }
                  disabled
                />

                <label className="form-check-label" htmlFor="humanApprovalRequired">
                  Human approval required before outreach
                </label>

                <div className="small text-muted">
                  Locked on for MVP safety.
                </div>
              </div>
            </div>
          </div>

          <div className="card mb-4">
            <div className="card-header">
              <strong>Follow-up Rules</strong>
            </div>

            <div className="card-body">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Days After First Contact
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="60"
                    value={settings.followUpDaysAfterFirstContact}
                    onChange={(e) =>
                      handleChange("followUpDaysAfterFirstContact", e.target.value)
                    }
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">
                    Days After Follow-up
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    min="1"
                    max="90"
                    value={settings.followUpDaysAfterFollowUp}
                    onChange={(e) =>
                      handleChange("followUpDaysAfterFollowUp", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? "Saving Settings..." : "Save Agent Settings"}
          </button>
        </form>
      )}
    </SalesPageLayout>
  );
}

export default AgentSettings;
